import {
  hasRole,
  requestContextValuesSchema,
  type RequestContextDto,
  type RequestContextValues,
  type Role,
} from '@agent-platform/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Braces, Check, CircleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog';
import { EmptyState } from '@/components/empty-state';
import { ErrorAlert } from '@/components/error-alert';
import { ListSearch, NoMatches, useListSearch } from '@/components/list-search';
import { PageHeader } from '@/components/page-header';
import { RequireTenant } from '@/components/require-tenant';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { formatRelative } from '@/lib/format';
import { cn } from '@/lib/utils';

export interface AgentVariables {
  agentId: string;
  name: string;
  draft: string[];
  published: string[];
}

/** Parses the JSON editor's text into request-context values, or explains what is wrong. */
export function parseContextJson(
  text: string,
): { ok: true; values: RequestContextValues } | { ok: false; error: string } {
  let raw: unknown;
  try {
    raw = text.trim() ? JSON.parse(text) : {};
  } catch {
    return { ok: false, error: 'This is not valid JSON.' };
  }
  const parsed = requestContextValuesSchema.safeParse(raw);
  if (!parsed.success) {
    const issue = parsed.error.issues[0];
    return {
      ok: false,
      error:
        issue?.code === 'invalid_type' && issue.path.length === 0
          ? 'Use a JSON object like {"user_tier": "pro"}.'
          : 'Values must be text, numbers or true/false, with keys like user_tier.',
    };
  }
  return { ok: true, values: parsed.data };
}

const EXAMPLE = JSON.stringify({ user_tier: 'pro', locale: 'en-GB', company: 'Acme' }, null, 2);

function PresetEditor({
  tenantId,
  preset,
  canEdit,
  onSaved,
}: {
  tenantId: string;
  preset: RequestContextDto | null;
  canEdit: boolean;
  onSaved: (preset: RequestContextDto) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [json, setJson] = useState(EXAMPLE);
  useEffect(() => {
    setName(preset?.name ?? '');
    setDescription(preset?.description ?? '');
    setJson(preset ? JSON.stringify(preset.values, null, 2) : EXAMPLE);
  }, [preset]);
  const parsed = parseContextJson(json);
  const save = useMutation({
    mutationFn: (values: RequestContextValues) =>
      preset
        ? api<RequestContextDto>(`/request-contexts/${preset.id}`, {
            method: 'PATCH',
            body: { description, values },
          })
        : api<RequestContextDto>('/request-contexts', {
            method: 'POST',
            body: { name, description, values },
          }),
    onSuccess: (saved) => {
      toast.success(`Saved ${saved.name}`);
      void queryClient.invalidateQueries({ queryKey: ['request-contexts', tenantId] });
      onSaved(saved);
    },
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{preset ? preset.name : 'New request context'}</CardTitle>
        <CardDescription>
          A JSON object of values. Agents read them as <code>{'{{key}}'}</code> in their system
          prompt; workflows as <code>{'{{context.key}}'}</code>.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <ErrorAlert error={save.error} />
        {!preset && (
          <div className="grid gap-1.5">
            <Label htmlFor="ctx-name">Name</Label>
            <Input
              id="ctx-name"
              className="font-mono"
              value={name}
              disabled={!canEdit}
              onChange={(e) => setName(e.target.value)}
              placeholder="pro-customer"
            />
          </div>
        )}
        <div className="grid gap-1.5">
          <Label htmlFor="ctx-description">Description</Label>
          <Input
            id="ctx-description"
            value={description}
            disabled={!canEdit}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="ctx-values">Values (JSON)</Label>
          <Textarea
            id="ctx-values"
            className={cn('min-h-56 font-mono text-xs', !parsed.ok && 'border-destructive')}
            value={json}
            disabled={!canEdit}
            onChange={(e) => setJson(e.target.value)}
            aria-invalid={!parsed.ok}
          />
          {!parsed.ok && <p className="text-destructive text-xs">{parsed.error}</p>}
        </div>
        {canEdit && (
          <Button
            className="justify-self-start"
            disabled={!parsed.ok || save.isPending || (!preset && !name)}
            onClick={() => parsed.ok && save.mutate(parsed.values)}
          >
            {preset ? 'Save changes' : 'Create'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function Coverage({
  agents,
  values,
}: {
  agents: AgentVariables[];
  values: Record<string, unknown> | null;
}) {
  const withVars = agents.filter((a) => a.draft.length > 0 || a.published.length > 0);
  if (withVars.length === 0)
    return (
      <p className="text-muted-foreground text-sm">
        No agent uses variables yet. Add <code>{'{{name}}'}</code> placeholders to a system prompt.
      </p>
    );
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Agent</TableHead>
          <TableHead>Variables (draft)</TableHead>
          <TableHead>Published</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {withVars.map((a) => (
          <TableRow key={a.agentId}>
            <TableCell>
              <Link
                to="/agents/$agentId"
                params={{ agentId: a.agentId }}
                className="hover:underline"
              >
                {a.name}
              </Link>
            </TableCell>
            {[a.draft, a.published].map((list, i) => (
              <TableCell key={i}>
                <div className="flex flex-wrap gap-1">
                  {list.length === 0 && <span className="text-muted-foreground">—</span>}
                  {list.map((v) => {
                    const covered = values ? v in values : null;
                    return (
                      <Badge
                        key={v}
                        variant={covered === false ? 'outline' : 'secondary'}
                        className="gap-1 font-mono font-normal"
                        title={
                          covered === null ? undefined : covered ? 'Set by this context' : 'Missing'
                        }
                      >
                        {covered === true && <Check aria-label="set" />}
                        {covered === false && <CircleAlert aria-label="missing" />}
                        {v}
                      </Badge>
                    );
                  })}
                </div>
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function RequestContextContent({ tenantId, role }: { tenantId: string; role: Role }) {
  const queryClient = useQueryClient();
  const canEdit = hasRole(role, 'admin');
  const [selectedId, setSelectedId] = useState<string | 'new' | null>(null);
  const presets = useQuery({
    queryKey: ['request-contexts', tenantId],
    queryFn: () => api<{ items: RequestContextDto[] }>('/request-contexts'),
  });
  const agentVars = useQuery({
    queryKey: ['request-context-agent-variables', tenantId],
    queryFn: () => api<{ items: AgentVariables[] }>('/request-contexts/agent-variables'),
  });
  const list = presets.data?.items ?? [];
  const search = useListSearch(list, (p) => [p.name, p.description, ...Object.keys(p.values)]);
  const selected =
    selectedId === 'new' ? null : (list.find((p) => p.id === selectedId) ?? list[0] ?? null);
  const remove = useMutation({
    mutationFn: (id: string) => api<void>(`/request-contexts/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setSelectedId(null);
      void queryClient.invalidateQueries({ queryKey: ['request-contexts', tenantId] });
    },
  });
  const [deleting, setDeleting] = useState(false);

  return (
    <>
      <PageHeader
        title="Request context"
        description="Per-request values like user tier, locale or account — the same idea as Mastra’s RequestContext. Pick one in the Playground or when running a workflow."
        actions={
          canEdit && (
            <Button variant="outline" onClick={() => setSelectedId('new')}>
              New context
            </Button>
          )
        }
      />
      <div className="grid gap-4 lg:grid-cols-[16rem_1fr]">
        <Card className="self-start">
          <CardHeader>
            <CardTitle className="text-base">Saved contexts</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-1">
            <ErrorAlert error={presets.error} />
            {presets.isPending ? (
              <Skeleton className="h-16" />
            ) : list.length === 0 ? (
              <p className="text-muted-foreground text-sm">None yet.</p>
            ) : (
              <>
                <ListSearch
                  className="mb-2"
                  value={search.query}
                  onChange={search.setQuery}
                  label="Search saved contexts"
                  placeholder="Search by name or key"
                />
                {search.filtered.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => setSelectedId(p.id)}
                    aria-current={selected?.id === p.id}
                    className={cn(
                      'hover:bg-muted flex items-start gap-2 rounded-md px-2 py-1.5 text-left text-sm',
                      selected?.id === p.id && selectedId !== 'new' && 'bg-muted font-medium',
                    )}
                  >
                    <Braces className="mt-0.5 size-4 shrink-0" aria-hidden />
                    <span className="grid min-w-0">
                      <span className="truncate font-mono text-xs">{p.name}</span>
                      <span className="text-muted-foreground text-xs">
                        {Object.keys(p.values).length} values · {formatRelative(p.updatedAt)}
                      </span>
                    </span>
                  </button>
                ))}
                {search.filtered.length === 0 && <NoMatches query={search.query} />}
              </>
            )}
          </CardContent>
        </Card>
        <div className="grid content-start gap-4">
          {list.length === 0 && selectedId !== 'new' && !presets.isPending ? (
            <EmptyState title="No request contexts yet">
              Save sets of values you test agents with — a free user, a pro customer, a German
              locale.
              {canEdit && (
                <div className="mt-3">
                  <Button onClick={() => setSelectedId('new')}>Create one</Button>
                </div>
              )}
            </EmptyState>
          ) : (
            <PresetEditor
              tenantId={tenantId}
              preset={selected}
              canEdit={canEdit}
              onSaved={(p) => setSelectedId(p.id)}
            />
          )}
          {selected && canEdit && selectedId !== 'new' && (
            <>
              <Button
                variant="destructive"
                className="justify-self-start"
                onClick={() => setDeleting(true)}
              >
                Delete {selected.name}
              </Button>
              <ConfirmDeleteDialog
                open={deleting}
                onOpenChange={setDeleting}
                title={`Delete ${selected.name}?`}
                description="This request context preset is deleted. This cannot be undone."
                onConfirm={() => remove.mutateAsync(selected.id)}
              />
            </>
          )}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Variables agents expect</CardTitle>
              <CardDescription>
                {selected && selectedId !== 'new'
                  ? `Checked against ${selected.name}.`
                  : 'Placeholders found in each agent’s system prompt.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ErrorAlert error={agentVars.error} />
              {agentVars.data ? (
                <Coverage
                  agents={agentVars.data.items}
                  values={selected && selectedId !== 'new' ? selected.values : null}
                />
              ) : (
                <Skeleton className="h-16" />
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}

export function RequestContextPage() {
  return (
    <RequireTenant>
      {(tenant) => <RequestContextContent tenantId={tenant.id} role={tenant.role} />}
    </RequireTenant>
  );
}
